import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import FocusLock from 'react-focus-lock';
import Button from '../../atoms/Button/Button';
import Input from '../../atoms/Input/Input';
import { searchValidationConfig } from '../../../types/ValidationTypes';
import {
    createEncodedSearchQueryString,
    parseSearchUrl,
    removeQuoteSearch,
    generateQuoteSearchEscapeQuotesInContent,
    parseQuery,
} from '../../../services/helpers/queryStringHelpers';
import { setPerPage } from '../../../store/userSettings/userSettingsSlice';
import { useAppDispatch } from '../../../store';
import { useB2BStore } from '../../../store/b2b/b2bStore';
import { searchOrganizations } from '../../../api/b2b/b2b';

type Inputs = {
    searchPhrase: string;
    filter: string[];
};

type SearchFormProps = {
    countryCode: string;
    setHasSearched?: (hasSearched: boolean) => void;
};

export const SearchForm: React.FC<SearchFormProps> = ({ countryCode, setHasSearched }) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<Inputs>();

    const {
        searchStarted,
        searchSucceeded,
        searchFailed,
        searchCanceled,
        isSearching,
        searchCancelToken,
    } = useB2BStore();

    const { pathname, search } = useLocation();
    const { page, sort, query, columns, perPage } = parseSearchUrl(search);

    const SEARCH_PHRASE = 'searchPhrase';
    const searchPhrase = register(SEARCH_PHRASE, searchValidationConfig);

    useEffect(() => {
        performSearch(query, page, false);
    }, [page, query]);

    const onSubmit = (form: Inputs) => {
        performSearch(form.searchPhrase, form.searchPhrase === query ? page : '1');
    };

    useEffect(() => {
        dispatch(setPerPage(perPage));
    }, [dispatch]);

    const performSearch = (q: string, p: string, nav: boolean = true) => {
        if (nav) {
            setHasSearched?.(true);
        }

        if (isSearching && searchCancelToken) searchCancelToken.cancel();

        const parsedQuery = parseQuery(q, true);

        nav && navigate(`${pathname}?query=${parsedQuery}`);
        searchOrganizations(
            parsedQuery,
            countryCode,
            searchStarted,
            searchSucceeded,
            searchFailed,
            searchCanceled
        );
    };

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)} data-testid="search-form">
                <SearchContainer>
                    <InputWrapper>
                        <FocusLock disabled={!isSearching}>
                            <Input
                                data-testid="search-input"
                                type="text"
                                name={searchPhrase.name}
                                defaultValue={removeQuoteSearch(query)}
                                setRef={searchPhrase.ref}
                                placeholder="What are you looking for?"
                                onChange={searchPhrase.onChange}
                            />
                        </FocusLock>
                    </InputWrapper>
                    <Button type="submit" primary large data-testid="submit-search-button">
                        Search
                    </Button>
                </SearchContainer>
            </form>
            {errors.searchPhrase && (
                <p className="error-message" role="alert">
                    {errors.searchPhrase?.message}
                </p>
            )}
        </>
    );
};

const SearchContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: ${props => props.theme.layout.gap.small};
    margin-bottom: 1rem;
`;

const InputWrapper = styled.div`
    width: 40rem;
`;
